import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { PaginatedNotificationListResponseList } from 'libs/mona/api/src/lib/model';
import { Observable, from, race } from 'rxjs';
import { defaultIfEmpty, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { AuthService } from '@mona/auth';
import { ConfigService } from '@mona/config';
import { currentEncounterId, isEmptyEncounterId, withCurrentEncounterId } from '@mona/pdms/data-access-combined';
import { isSameEncounterFilter } from '@mona/shared/utils';
import { makeDefaultAsyncActionEffect, handleEffectError } from '@mona/store';
import { NotificationsState } from '../../entities';
import { NotificationsApi, NotificationsWsService } from '../../infrastructure';
import { NotificationsAction } from '../actions/notifications.actions';

/**
 * Notifications effects
 */
@Injectable()
export class NotificationsEffects {
    /**
     * Load init notifications effect
     */
    loadInitNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsAction.loadPageableNotificationsAction),
            filter(action => !action.isLoadMore),
            switchMap(({ encounterId, url, readStatus }) => {
                const pageSize = this.configService.get('notificationsPageSize') || 20;
                return race(
                    isEmptyEncounterId(this.store).pipe(
                        take(1),
                        map(() =>
                            NotificationsAction.loadPageableNotificationsActionCancelled({
                                reason: 'encounter-cleared',
                            }),
                        ),
                    ),
                    this.notificationsApi.getPageableNotifications(encounterId, url, pageSize, readStatus).pipe(
                        isSameEncounterFilter(currentEncounterId(this.store), encounterId),
                        switchMap((result: PaginatedNotificationListResponseList.Model) => [
                            NotificationsAction.loadPageableNotificationsActionSuccess({
                                payload: result,
                                readStatus,
                                isLoadMore: false,
                            }),
                        ]),
                        handleEffectError(NotificationsAction.loadPageableNotificationsActionFailed),
                        defaultIfEmpty(
                            NotificationsAction.loadPageableNotificationsActionCancelled({
                                reason: 'encounter-changed',
                            }),
                        ),
                    ),
                );
            }),
        ),
    );

    /**
     * Load more notifications effect
     */
    loadMoreNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsAction.loadPageableNotificationsAction),
            filter(action => action.isLoadMore),
            withCurrentEncounterId(),
            switchMap(([{ url, readStatus }, encounterId]) =>
                race(
                    isEmptyEncounterId(this.store).pipe(
                        take(1),
                        map(() =>
                            NotificationsAction.loadPageableNotificationsActionCancelled({
                                reason: 'encounter-cleared',
                            }),
                        ),
                    ),
                    this.notificationsApi
                        .getPageableNotifications(
                            encounterId,
                            url,
                            this.configService.get('notificationsPageSize') || 20,
                            readStatus,
                        )
                        .pipe(
                            isSameEncounterFilter(currentEncounterId(this.store), encounterId),
                            switchMap((result: PaginatedNotificationListResponseList.Model) => [
                                NotificationsAction.loadPageableNotificationsActionSuccess({
                                    payload: result,
                                    readStatus,
                                    isLoadMore: true,
                                }),
                            ]),
                            handleEffectError(NotificationsAction.loadPageableNotificationsActionFailed),
                            defaultIfEmpty(
                                NotificationsAction.loadPageableNotificationsActionCancelled({
                                    reason: 'encounter-changed',
                                }),
                            ),
                        ),
                ),
            ),
        ),
    );

    /**
     * Change pageable notifications read status effect
     */
    changePageableNotificationsReadStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsAction.changePagedNotificationReadStatusAction),
            switchMap(action =>
                this.notificationsApi.changeNotificationReadStatus(action.notification.id, action.readStatus).pipe(
                    map(notification =>
                        NotificationsAction.changePagedNotificationReadStatusActionSuccess({
                            notification,
                            readStatus: action.readStatus,
                        }),
                    ),
                ),
            ),
        ),
    );

    /**
     * Mark multiple notifications as read effect
     */
    markMultipleNotificationsAsRead$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsAction.markMultipleNotificationsAsReadAction),
            switchMap(action =>
                this.notificationsApi
                    .markMultipleNotificationsAsRead(action.notificationIds)
                    .pipe(
                        map(notifications =>
                            NotificationsAction.markMultipleNotificationsAsReadActionSuccess({ notifications }),
                        ),
                    ),
            ),
        ),
    );

    /**
     * Load has unread notifications effect
     */
    loadHasUnreadNotifications$ = createEffect(() =>
        this.actions$.pipe(
            ofType(NotificationsAction.loadHasUnreadNotificationsAction.action),
            switchMap(action =>
                makeDefaultAsyncActionEffect(
                    this.notificationsApi.getUnreadNotificationStatus(action.encounterId),
                    NotificationsAction.loadHasUnreadNotificationsAction,
                ),
            ),
        ),
    );

    /**
     * Constructor
     *
     * @param store Store
     * @param actions$ Actions
     * @param notificationsApi NotificationsApi
     * @param notificationsWsService
     * @param authService AuthService
     * @param configService ConfigService
     */
    constructor(
        private store: Store<{ notifications: NotificationsState }>,
        private actions$: Actions,
        private notificationsApi: NotificationsApi,
        private notificationsWsService: NotificationsWsService,
        private authService: AuthService,
        private configService: ConfigService,
    ) {}

    /**
     * Handles encounter notifications
     *
     * @param encounterId EntityId<Encounter>
     */
    private handleNotifications(encounterId: string): void {
        this.notificationsWsService.disconnect();
    }

    /**
     * Reconnects ws after connection is reestablished
     * Had to reconnect after connection reestablished as after long offline status
     * existing ws reconnection logic does not work anymore
     */
    private handleConnectionReestablished(): void {
        //
    }
}
